let lastUserActivity = Date.now();
let isTracking = false;

// Track various user interactions
const userEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'keydown'];

const updateLastActivity = (event) => {
  lastUserActivity = Date.now();
  // Throttle logging to avoid spam - only log every 10 seconds
  if (!updateLastActivity.lastLogTime || Date.now() - updateLastActivity.lastLogTime > 10000) {
    //console.log('🕒 [USER ACTIVITY] User activity detected. Updating last activity timestamp.', event.type);
    updateLastActivity.lastLogTime = Date.now();
  }
};

/**
 * Initialize user activity tracking
 * Sets up event listeners to track user interactions
 */
export const initUserActivityTracking = () => {
  if (isTracking) {
    return; // Already tracking
  }

  userEvents.forEach(event => {
    document.addEventListener(event, updateLastActivity, { passive: true });
  });
  
  isTracking = true;
};

/**
 * Check if user has been active within the specified time
 * @param {number} timeWindowMs - Time window in milliseconds (default 30 minutes)
 * @returns {boolean} True if user has been active within the time window
 */
export const hasRecentUserActivity = (timeWindowMs = 30 * 60 * 1000) => {
  return (Date.now() - lastUserActivity) < timeWindowMs;
};

/**
 * Get time since last user activity in milliseconds
 * @returns {number} Milliseconds since last activity
 */
export const getTimeSinceLastActivity = () => {
  return Date.now() - lastUserActivity;
};

/**
 * Get time since last user activity in a human-readable format
 * @returns {object} Object with minutes and seconds since last activity
 */
export const getFormattedTimeSinceLastActivity = () => {
  const timeSinceMs = getTimeSinceLastActivity();
  const minutes = Math.floor(timeSinceMs / (60 * 1000));
  const seconds = Math.floor((timeSinceMs % (60 * 1000)) / 1000);
  
  return { minutes, seconds };
};

/**
 * Manually update the last activity timestamp
 * Useful for programmatically marking user activity
 */
export const markUserActivity = () => {
  updateLastActivity();
};

/**
 * Check if the user is currently considered active
 * @param {number} inactivityThresholdMs - Threshold for considering user inactive (default 15 minutes)
 * @returns {boolean} True if user is considered active
 */
export const isUserActive = (inactivityThresholdMs = 15 * 60 * 1000) => {
  return hasRecentUserActivity(inactivityThresholdMs);
};

/**
 * Get the last activity timestamp
 * @returns {number} Timestamp of last activity
 */
export const getLastActivityTimestamp = () => {
  return lastUserActivity;
};

/**
 * Cleanup user activity tracking
 * Removes all event listeners
 */
export const cleanupUserActivityTracking = () => {
  if (!isTracking) {
    return; // Not tracking
  }

  userEvents.forEach(event => {
    document.removeEventListener(event, updateLastActivity);
  });
  
  isTracking = false;
};

/**
 * Reset activity tracking
 * Useful for testing or when user logs in/out
 */
export const resetUserActivityTracking = () => {
  lastUserActivity = Date.now();
};

/**
 * Get activity tracking status
 * @returns {boolean} True if tracking is active
 */
export const isActivityTrackingActive = () => {
  return isTracking;
};